import React, { useState, useEffect, useRef } from "react";
import { Typography, Checkbox, Box } from "@mui/material";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import { setStep } from "../../globalRedux/actions";
import { getTransactionHistory, hideError } from "../redux/action";
import "./styles.scss";
import "../styles/index.scss"; // global styles
import styles from "./styles";

import {
  BottomNav,
  CustomButton,
  Dropdown,
  TransactionHistory,
  TransactionInfiniteLoader,
} from "../../components";
import { icons } from "../../constants";
import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import { groupTransactionHistoryByMonth } from "../../utils/functions/groupTransactionHistoryByMonth";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

const Transaction = props => {
  const {
    loadingTransactionHistory,
    transactionHistory,
    getTransactionHistory,
  } = props;

  const [openFilter, setOpenFilter] = useState(false);
  const [timePeriod, setTimePeriod] = useState({
    from: null,
    to: null,
  });
  const [options, setOptions] = useState({
    type: {
      buy: false, // deposit
      transfer: false, // transfer
      sell: false, // withdrawal,
      rspbuy: false, // rsp,
      switching: false, // switching
    },
    category: {
      MPInvest: false,
      MPSave: false,
      MPRetire: false,
    },
  });

  const [transactions, setTransactions] = useState([]);
  const [filterBtn, setFilterBtn] = useState(false);
  const [payload, setPayload] = useState({
    from: "",
    to: "",
    type: "",
    category: "",
    size: 10,
    page: 1,
  });

  const pageNumber = useRef(1);
  const transactionsLength = useRef(0);

  const { numPages, totalPages } = transactionHistory;

  const flattenAndDistributeTransactions = trxs => {
    const distributeMultipleFundDetails = trxs.reduce(
      (prev, { details, ...rest }) => {
        const isGoPlusTrx = rest.source === "GO+ Deeplink";
        if (details.length > 1 && rest.type === "Buy") {
          for (const d of details) {
            prev.push({
              ...rest,
              plan: { ...rest.plan, planAlias: d.planAlias },
              details: [d],
              amount: d.amount,
              actualAmt: d.actualAmt,
              unit: d.unit,
              status: d.status,
              actualUnitPrice: d.actualUnitPrice,
              navDate: d.navDate,
              isSubRecord: true,
              completedDate: d?.completedDate || rest.completedDate,
              salesChargeAmount: isGoPlusTrx
                ? d?.feeAmount
                : rest.salesChargeAmount,
              salesChargeCcy: isGoPlusTrx ? d?.feeCcy : rest.salesChargeCcy,
              salesChargePercent: isGoPlusTrx
                ? d?.feePercent
                : rest.salesChargePercent,
            });
          }
        } else {
          prev.push({
            details,
            ...rest,
            salesChargeAmount: isGoPlusTrx
              ? details?.at(0)?.feeAmount
              : rest.salesChargeAmount,
            salesChargeCcy: isGoPlusTrx
              ? details?.at(0)?.feeCcy
              : rest.salesChargeCcy,
            salesChargePercent: isGoPlusTrx
              ? details?.at(0)?.feePercent
              : rest.salesChargePercent,
          });
        }
        return prev;
      },
      []
    );

    transactionsLength.current += distributeMultipleFundDetails.length;

    setTransactions(prev => [
      ...groupTransactionHistoryByMonth(distributeMultipleFundDetails, prev),
    ]);
  };

  const callTrx = page => {
    const { from, to, type, size, category } = payload;

    getTransactionHistory(
      {
        page,
        size,
        from,
        to,
        type,
        category,
      },
      flattenAndDistributeTransactions
    );
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
    callTrx(pageNumber.current);
  }, []);

  useEffect(() => {
    if (timePeriod.from instanceof Date && timePeriod.to instanceof Date) {
      if (timePeriod.from.getTime() > timePeriod.to.getTime()) {
        setFilterBtn(true);
      } else {
        setFilterBtn(false);
      }
    }
  }, [timePeriod]);

  const handleChangeCheck = field => ({ target: { name } }) => {
    dataLayerGTM("click_tag", "Click", "Checkbox", `Check ${name}`);
    setOptions(prevState => {
      prevState[field] = {
        ...prevState[field],
        [name]: !prevState[field][name],
      };
      return { ...prevState };
    });
  };

  /**
   *
   * get keys of options, filter the `true` one and join it with `,`
   *
   * @param {"category" | "type"} field
   * @returns concatenation of checked option
   */
  const getCheckedOptions = field => {
    return Object.keys(options[field])
      .filter(key => options[field][key])
      .join(",");
  };

  const handleApplyFilter = () => {
    setTransactions([]);

    const endMonthExist = moment(timePeriod.to)
      .endOf("month")
      .format("YYYY-MM-DD");

    pageNumber.current = 1;

    let type = getCheckedOptions("type");

    if (type.includes("switching")) {
      type = type.replace(
        "switching",
        "SwitchIn,SwitchOut,SwitchAll,ManSwitchIn,ManSwitchOut,ManSwitchAll,TransferIn,TransferOut,TransferAll,CorrSWI,CorrSWO"
      );
    }

    if (type.includes("buy")) {
      type = type.replace("buy", "Buy,ManBuy,CorrBuy");
    }

    if (type.includes("sell")) {
      type = type.replace(
        "sell",
        "Sell,ManSell,CorrSell,FullSell,ManFullSell,CoolOff"
      );
    }

    const payload = {
      from: timePeriod.from ? moment(timePeriod.from).format("YYYY-MM-DD") : "",
      to: timePeriod.to ? endMonthExist : "",
      category: getCheckedOptions("category"),
      type,
      size: 10,
      page: pageNumber.current,
    };

    setPayload(payload);
    getTransactionHistory(payload, flattenAndDistributeTransactions);
    setOpenFilter(false);
  };

  return (
    <Box sx={{ backgroundColor: "#f6f6f8" }}>
      {/* Dropdown */}
      <Dropdown
        bottomHeight={({ maxHeight }) => maxHeight - maxHeight / 10}
        isOpen={openFilter}
        handleDismiss={() => {
          setOpenFilter(false);
        }}
        footer={
          <CustomButton
            text="Apply Filter"
            variant="contained"
            color="#fff"
            backgroundColor="#1278cc"
            onClick={handleApplyFilter}
            disabled={filterBtn}
          />
        }
      >
        <Box sx={{ margin: "24px 16px" }}>
          <Typography
            color="secondary"
            sx={{
              fontSize: "20px",
              fontWeight: "700",
              lineHeight: 1.4,
            }}
          >
            Transaction Filter
          </Typography>
        </Box>
        <Box sx={{ ...styles.column, flex: 1, margin: "0 16px" }}>
          <Typography color="textPrimary" sx={styles.formLabel}>
            Time Period
          </Typography>
          <Box
            sx={{
              ...styles.row,
              justifyContent: "space-between",
              margin: "8px 0 14px",
            }}
          >
            <Box sx={{ ...styles.column, marginRight: "8px" }}>
              <Typography color="textPrimary" sx={styles.formLabel}>
                From
              </Typography>
              <div className="inputField">
                <DatePicker
                  selected={timePeriod.from}
                  onChange={date => {
                    setTimePeriod({
                      from: date,
                      to: timePeriod.to,
                    });
                  }}
                  onFocus={e => e.target.blur()}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  popperPlacement="bottom-start"
                  className="date"
                  maxDate={new Date()}
                />
                <CalendarTodayOutlinedIcon
                  sx={{ color: "#1278cc", fontSize: 18 }}
                />
              </div>
            </Box>
            <Box sx={{ ...styles.column, marginLeft: "8px" }}>
              <Typography color="textPrimary" sx={styles.formLabel}>
                To
              </Typography>
              <div className="inputField">
                <DatePicker
                  selected={timePeriod.to}
                  onChange={date =>
                    setTimePeriod({ from: timePeriod.from, to: date })
                  }
                  onFocus={e => e.target.blur()}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  popperPlacement="bottom-end"
                  className="date"
                  maxDate={new Date()}
                />
                <CalendarTodayOutlinedIcon
                  sx={{ color: "#1278cc", fontSize: 18 }}
                />
              </div>
            </Box>
          </Box>
          <div>
            <Typography color="textPrimary" sx={styles.formLabel}>
              Asset Type
            </Typography>
            <Box sx={{ marginTop: "8px" }}>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Savings
                </Typography>
                <Checkbox
                  checked={options.category.MPSave}
                  name="MPSave"
                  onChange={handleChangeCheck("category")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Invest
                </Typography>
                <Checkbox
                  checked={options.category.MPInvest}
                  name="MPInvest"
                  onChange={handleChangeCheck("category")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Retirement
                </Typography>
                <Checkbox
                  checked={options.category.MPRetire}
                  name="MPRetire"
                  onChange={handleChangeCheck("category")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
            </Box>
          </div>
          <div>
            <Typography color="textPrimary" sx={styles.formLabel}>
              Transaction Type
            </Typography>
            <Box sx={{ marginTop: "8px" }}>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Auto Cash In
                </Typography>
                <Checkbox
                  checked={options.type.rspbuy}
                  name="rspbuy"
                  onChange={handleChangeCheck("type")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Cash In
                </Typography>
                <Checkbox
                  checked={options.type.buy}
                  name="buy"
                  onChange={handleChangeCheck("type")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Switching
                </Typography>
                <Checkbox
                  checked={options.type.switching}
                  name="switching"
                  onChange={handleChangeCheck("type")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
              <Box
                sx={{
                  ...styles.row,
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  border: "solid 1px rgba(51, 51, 51, 0.2)",
                  padding: "3px 16px",
                  marginBottom: "16px",
                }}
              >
                <Typography color="textPrimary" sx={{ fontWeight: "700" }}>
                  Cash Out
                </Typography>
                <Checkbox
                  checked={options.type.sell}
                  name="sell"
                  onChange={handleChangeCheck("type")}
                  sx={{
                    color: "#0091da",
                    "& .MuiSvgIcon-root": { fontSize: 24 },
                  }}
                />
              </Box>
            </Box>
          </div>
        </Box>
      </Dropdown>

      <Box sx={{ ...styles.row, ...styles.nav }}>
        <Typography
          color="secondary"
          sx={{
            fontSize: "20px",
            fontWeight: "bold",
            lineHeight: 1.4,
            textTransform: "none",
          }}
        >
          Transaction history
        </Typography>
        <Box
          sx={styles.row}
          onClick={() => {
            dataLayerGTM(
              "click_tag",
              "Click",
              "Button",
              "Transaction Filter Button"
            );
            setOpenFilter(true);
          }}
        >
          <img src={icons.filter} alt="filter" />
          <Typography color="primary" sx={styles.filterLabel}>
            Filter
          </Typography>
        </Box>
      </Box>

      {/* Content */}

      <Box
        sx={{
          ...styles.column,
          margin: "auto 16px",
          minHeight: "calc(100vh - 104px)",
        }}
      >
        <InfiniteScroll
          dataLength={transactionsLength.current}
          next={() => {
            if (window.scrollY === 0) return;

            callTrx((pageNumber.current += 1));
          }}
          hasMore={numPages < totalPages}
          loader={
            loadingTransactionHistory &&
            pageNumber.current > 1 && <TransactionInfiniteLoader />
          }
          style={{ overflowX: "hidden" }}
        >
          <TransactionHistory
            transactions={transactions}
            loading={loadingTransactionHistory && pageNumber.current === 1}
          />
        </InfiniteScroll>
      </Box>

      {/* Bottom Navigation */}
      <Box sx={{ position: "sticky", bottom: 0, width: "100%" }}>
        <BottomNav />
      </Box>
    </Box>
  );
};

const withConnect = connect(
  state => ({
    isModalOpen: state.goal.isModalOpen,
    errorMessage: state.goal.errorMessage,
    transactionHistory: state.goal.transactionHistory,
    loadingTransactionHistory: state.goal.loadingTransactionHistory,
    profile: state.goal.profile,
  }),
  {
    getTransactionHistory,
    setStep,
    hideError,
  }
);

export default compose(
  withRouter,
  withConnect
)(Transaction);
